// 📂 src/shared/utils/money-utils.ts

import { type CurrenciesType, type Tasa } from '@/shared/schema';

export const MoneyUtils = {
  // 1. Redondeo Físico (Snap to 2 decimals)
  round: (amount: number): number => {
    return Math.round((amount + Number.EPSILON) * 100) / 100;
  },

  // 2. Currencies that support floating/conversion logic (The "Trio")
  SUPPORTED_MULTI_CURRENCY: ['USD', 'EUR', 'BS'] as CurrenciesType[],

  isMultiCurrency: (currency: CurrenciesType): boolean => {
    return MoneyUtils.SUPPORTED_MULTI_CURRENCY.includes(currency);
  },

  // 3. Conversión inteligente con todas las tasas disponibles
  convert: (
    amount: number,
    from: CurrenciesType,
    to: CurrenciesType,
    rates: Tasa,
  ): number => {
    // Si es la misma moneda, no hay conversión
    if (from === to) return MoneyUtils.round(amount);

    // Validar que rates existe
    if (!rates) {
      return amount;
    }

    try {
      // 1. Map all rates to a USD base (1.0 USD = X units of other currency)
      const ratesMap: Record<CurrenciesType, number> = {
        USD: 1,
        BS: rates.currentTasa,
        EUR: 1 / rates.eurToUsd,
        COP: rates.copTasa || 4000,
        PEN: 1, // Standalone
        MXN: 1, // Standalone
        ARS: 1, // Standalone
        CLP: 1, // Standalone
        UYU: 1, // Standalone
      };

      // 2. Convert Source -> USD
      const amountUsd = amount / (ratesMap[from] || 1);

      // 3. Convert USD -> Target
      const finalAmount = amountUsd * (ratesMap[to] || 1);

      return MoneyUtils.round(finalAmount);
    } catch (error) {
      console.error('❌ Error en conversión:', error);
      return amount;
    }
  },

  // 4. Formateador con soporte para todas las monedas
  format: (amount: number, currency: CurrenciesType): string => {
    const config: Record<CurrenciesType, { locale: string; code: string }> = {
      USD: { locale: 'en-US', code: 'USD' },
      EUR: { locale: 'de-DE', code: 'EUR' },
      BS: { locale: 'es-VE', code: 'VES' },
      COP: { locale: 'es-CO', code: 'COP' },
      PEN: { locale: 'es-PE', code: 'PEN' },
      MXN: { locale: 'es-MX', code: 'MXN' },
      ARS: { locale: 'es-AR', code: 'ARS' },
      CLP: { locale: 'es-CL', code: 'CLP' },
      UYU: { locale: 'es-UY', code: 'UYU' },
    };

    // Validar que la moneda existe
    if (!config[currency]) {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
      }).format(amount);
    }

    const { locale, code } = config[currency];

    // Ajuste especial para el símbolo de Soles (PEN) que a veces sale como S/ o PEN
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: code,
      minimumFractionDigits: 2,
    }).format(amount);
  },

  // 5. Metadatos de Monedas
  getMetadata: (currency: CurrenciesType) => {
    const metadata: Record<
      CurrenciesType,
      { symbol: string; flag: string; name: string }
    > = {
      USD: { symbol: '$', flag: '🇺🇸', name: 'Dólares' },
      EUR: { symbol: '€', flag: '🇪🇺', name: 'Euros' },
      BS: { symbol: 'Bs', flag: '🇻🇪', name: 'Bolívares' },
      COP: { symbol: '$', flag: '🇨🇴', name: 'Pesos' },
      PEN: { symbol: 'S/', flag: '🇵🇪', name: 'Soles' },
      MXN: { symbol: '$', flag: '🇲🇽', name: 'Pesos' },
      ARS: { symbol: '$', flag: '🇦🇷', name: 'Pesos' },
      CLP: { symbol: '$', flag: '🇨🇱', name: 'Pesos' },
      UYU: { symbol: '$', flag: '🇺🇾', name: 'Pesos' },
    };
    return metadata[currency] || metadata.USD;
  },

  // Mantenemos métodos legacy por compatibilidad
  toBs: (amountUsd: number, tasa: number): number => {
    return MoneyUtils.round(amountUsd * tasa);
  },

  toUsd: (amountBs: number, tasa: number): number => {
    if (tasa <= 0) return 0;
    return MoneyUtils.round(amountBs / tasa);
  },

  calcCommission: (total: number, percentage: number): number => {
    return MoneyUtils.round(total * (percentage / 100));
  },
};
